import Logger from "../../../core/internal/logger"
import { IStorage } from "../../../core/internal/storage/Storage"
import ObjectUtil from "../../../core/internal/util/ObjectUtil"
import { InAppMessageDelay } from "../delay/InAppMessageDelay"

// serializable data from InAppMessageDelay
export interface InAppMessageDelayStorageEntity {
  requestedAt: InAppMessageDelay["requestedAt"]
  schedule: Pick<
    InAppMessageDelay["schedule"],
    "dispatchId" | "inAppMessageKey" | "identifiers" | "reason" | "eventBasedContext"
  > & {
    time: Pick<InAppMessageDelay["schedule"]["time"], "startedAt" | "deliverAt">
  }
}

export interface InAppMessageDelayStorageItem {
  [inAppMessageKey: string]: InAppMessageDelayStorageEntity[]
}

export interface InAppMessageDelayStorage {
  set(inAppMessageKey: number, dispatchId: string, delay: InAppMessageDelayStorageEntity): void

  get(inAppMessageKey: number, dispatchId: string): InAppMessageDelayStorageEntity | null

  remove(inAppMessageKey: number, dispatchId: string): void

  values(): InAppMessageDelayStorageEntity[]

  clear(): void
}

export class InAppMessageDelayLocalStorage implements InAppMessageDelayStorage {
  private storageKey = "iam-delay"

  constructor(private readonly storage: IStorage) {}

  private getDelayStorageItem(): InAppMessageDelayStorageItem | null {
    const storageItem = this.storage.getItem(this.storageKey)
    if (ObjectUtil.isNullOrUndefined(storageItem)) return null

    try {
      return JSON.parse(storageItem) as InAppMessageDelayStorageItem
    } catch (error) {
      Logger.log.warn(`Failed to get delay storage item: ${error}`)
      return null
    }
  }

  get(inAppMessageKey: number, dispatchId: string): InAppMessageDelayStorageEntity | null {
    const delayStorageItem = this.getDelayStorageItem()
    if (ObjectUtil.isNullOrUndefined(delayStorageItem)) return null

    return delayStorageItem[inAppMessageKey]?.find((delay) => delay.schedule.dispatchId === dispatchId) ?? null
  }

  set(inAppMessageKey: number, dispatchId: string, delay: InAppMessageDelayStorageEntity): void {
    const prevItem = this.getDelayStorageItem()
    if (ObjectUtil.isNullOrUndefined(prevItem)) {
      Logger.log.debug(`No delay storage item found. create new item`)
      this.storage.setItem(this.storageKey, JSON.stringify({ [inAppMessageKey]: [delay] }))
      return
    }

    const targetEntities = prevItem[inAppMessageKey] ?? []
    targetEntities.push(delay)
    prevItem[inAppMessageKey] = targetEntities

    this.storage.setItem(this.storageKey, JSON.stringify(prevItem))
  }

  remove(inAppMessageKey: number, dispatchId: string): void {
    const prevItem = this.getDelayStorageItem()
    const targetEntities = prevItem?.[inAppMessageKey]

    if (ObjectUtil.isNullOrUndefined(prevItem) || ObjectUtil.isNullOrUndefined(targetEntities)) {
      Logger.log.debug(`No delay storage item found for ${inAppMessageKey}. do nothing`)
      return
    }

    prevItem[inAppMessageKey] = targetEntities.filter((delay) => delay.schedule.dispatchId !== dispatchId)
    if (prevItem[inAppMessageKey].length === 0) {
      delete prevItem[inAppMessageKey]
    }

    this.storage.setItem(this.storageKey, JSON.stringify(prevItem))
  }

  values(): InAppMessageDelayStorageEntity[] {
    const delayStorageItem = this.getDelayStorageItem()
    if (ObjectUtil.isNullOrUndefined(delayStorageItem)) {
      Logger.log.debug(`No delay storage item found. do nothing`)
      return []
    }

    return Object.values(delayStorageItem).flat()
  }

  clear(): void {
    this.storage.removeItem(this.storageKey)
  }
}
